import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useModalManager } from '@/components/common/Modal';
import SubmitModal from '@/components/ui/Modal/SubmitModal';
import Icon from '@/components/ui/Icon';
import Text from '@/components/ui/Text';
import cn from '@/utils/cn';
import Tooltip from '@/components/ui/Tooltip';
import { IImage } from '../interfaces';
import {
  useEnableOnHover,
  useImages,
  useImageModels,
  useMergedVersions,
  useRevertMutation,
} from './MergeHistoryModal.utils';
import { ModalContent } from './ModalContent';
import { SuccessMessage } from './SuccessMessage';

interface MergeHistoryModalProps {
  image: IImage;
}

export const MergeHistoryModal = ({ image }: MergeHistoryModalProps) => {
  const [revertedVersions, setRevertedVersions] = useState([]);
  const { close, isOpen, open } = useModalManager();
  const { isEnabled, ref, setHasHovered } = useEnableOnHover();
  const { data: versions } = useMergedVersions({ image, isEnabled });
  const { isLoading, mutateAsync } = useRevertMutation({ image, setRevertedVersions });

  const form = useForm({ defaultValues: { imageIds: [] } });
  const imageIds = form.watch('imageIds');

  useImageModels({ image, isEnabled });
  useImages({ image, isEnabled });

  const closeHandler = () => {
    close();
    setRevertedVersions([]);
  };

  const params = new URLSearchParams(window.location.href);
  const imageId = params.get('id[eq]');

  useEffect(() => {
    if (imageId !== image.id.toString()) return;

    open();
    setHasHovered(true);
  }, [window.location.href]);

  const cannotUseModal = (versions && versions.length === 0) || image.num_of_uses < 1;

  return (
    <FormProvider {...form}>
      <Tooltip.Provider>
        <Tooltip delayDuration={150}>
          <Tooltip.Portal>
            <Tooltip.Content
              className="tw-z-9999"
              disabled={!cannotUseModal}
              theme="white"
            >
              This image does not have merge history or is not currently in use.
              <Tooltip.Arrow />
            </Tooltip.Content>
          </Tooltip.Portal>
          <Tooltip.Trigger asChild>
            <span className="tw-w-full">
              <button
                ref={ref}
                className={cn('dropdown-item full-size tw-flex tw-items-center tw-gap-2')}
                disabled={cannotUseModal}
                type="button"
                onClick={open}
              >
                <Icon className="-tw-ml-0.5" icon="History" />
                View Merge History
              </button>
            </span>
          </Tooltip.Trigger>
        </Tooltip>
      </Tooltip.Provider>
      <SubmitModal
        cancelText={revertedVersions.length === 0 ? undefined : 'Close'}
        className="tw-w-[850px]"
        close={closeHandler}
        headerText={(
          <Text className="!tw-leading-none" size="lg" tag="span">
            {`Merge History for ${image.image_en_file_name}`}
          </Text>
        )}
        isLoading={isLoading}
        isOpen={isOpen}
        isValid={imageIds.length > 0}
        submitText={`Revert Merge (${imageIds.length})`}
        onSubmit={async (e) => {
          e.preventDefault();

          await mutateAsync({ ids: form.getValues().imageIds });
          form.reset();
        }}
      >
        {revertedVersions.length > 0 && <SuccessMessage revertedVersions={revertedVersions} />}
        <ModalContent image={image} />
      </SubmitModal>
    </FormProvider>
  );
};
